import { SharingTipOverlay } from './SharingTipOverlay';
import { onOpenSharingDialogClicked } from '../utils/onOpenSharingDialogClicked';
import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { InfoBarMessageRank } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { lazyIgnoreSharingTip, LinkActionStatus, SharingLinkInfo } from 'owa-link-data';
import { refreshExpirationDate, SharingTipAction, SharingTipInfo } from 'owa-sharing-data';
import strings from 'owa-strings/owa-mail-strings';
import * as React from 'react';
import {
    getInfoIconTooltip,
    getDisplayString,
} from '../utils/contextMenu/sharingTipContextMenuItem/sharingTipStrings';
import {
    logChangePermsSharingTipDatapoint,
    logIgnoreSharingTipDatapoint,
    logRefreshExpirationDatapoint,
} from '../utils/contextMenu/sharingTipContextMenuItem/logSharingTipDatapoints';

const menuItemStyles = require('./LinkContextMenuCustomItems.scss');
const sharingTipStyles = require('./SharingTipContextMenuItem.scss');
const classNames = require('classnames/bind').bind(sharingTipStyles); // bind needed for conditional styles

export interface SharingTipContextMenuItemProps {
    sharingLinkInfo: SharingLinkInfo;
    sharingIssue: SharingTipInfo;
}

@observer
export class SharingTipContextMenuItem extends React.Component<SharingTipContextMenuItemProps, {}> {
    render() {
        const { sharingIssue, sharingLinkInfo } = this.props;
        const conditionalStyles = {
            error: sharingIssue.messageRank === InfoBarMessageRank.Error,
        };
        const infoTooltip = getInfoIconTooltip(sharingIssue.id, sharingLinkInfo.linkActionStatus);
        return (
            <div
                className={menuItemStyles.menuItemContainer}
                aria-label={sharingIssue.sharingTipString}
                aria-posinset={1}
                aria-setsize={7}
                aria-disabled="false"
                role="menuitem"
                onClick={() => null}
                tabIndex={0}>
                <SharingTipOverlay sharingLinkInfo={sharingLinkInfo} sharingIssue={sharingIssue} />
                <div className={menuItemStyles.iconContainer}>
                    <Icon
                        iconName={ControlIcons.Info}
                        className={classNames(sharingTipStyles.sharingTipIcon, conditionalStyles)}
                        title={infoTooltip}
                    />
                </div>
                {getSharingTipMessageAndActions(sharingLinkInfo, sharingIssue)}
            </div>
        );
    }
}

function getSharingTipMessageAndActions(
    sharingLinkInfo: SharingLinkInfo,
    sharingIssue: SharingTipInfo
): JSX.Element {
    // Note - as we add more acitons we will probably want to move to a mapping from sharingTIpAction to button.
    const canIgnore = sharingIssue.sharingTipActions.indexOf(SharingTipAction.ignore) !== -1;
    const canRefresh =
        sharingIssue.sharingTipActions.indexOf(SharingTipAction.refreshExpiration) !== -1 &&
        sharingLinkInfo.sharingInfo.canRefresh;
    const canChangePerms =
        sharingIssue.sharingTipActions.indexOf(SharingTipAction.changePermission) !== -1 &&
        isFeatureEnabled('doc-attachment-sharingDialog');
    const refreshString =
        sharingLinkInfo.linkActionStatus === LinkActionStatus.refreshFailed
            ? strings.SuggestActionRetryRefreshingTheLink
            : strings.SuggestActionRefreshTheLink;

    // We over-write the sharing tip string for when the refresh fails.
    const displayString: string = getDisplayString(
        sharingIssue.id,
        sharingIssue.sharingTipString,
        sharingLinkInfo.linkActionStatus,
        sharingLinkInfo.sharingInfo.expirationDate
    );

    return (
        <div className={menuItemStyles.text}>
            <span className={sharingTipStyles.paddingRight}>{displayString}</span>
            {canRefresh && (
                <>
                    <button
                        className={sharingTipStyles.actionButton}
                        onClick={event => {
                            refreshExpirationDate(sharingLinkInfo.linkId);
                            logRefreshExpirationDatapoint(sharingIssue.id);
                            event.stopPropagation();
                        }}>
                        {refreshString}
                    </button>
                    <span className={sharingTipStyles.divider}> {' | '} </span>
                </>
            )}
            {canChangePerms && (
                <>
                    <button
                        className={sharingTipStyles.actionButton}
                        onClick={event => {
                            onOpenSharingDialogClicked(sharingLinkInfo);
                            logChangePermsSharingTipDatapoint(sharingIssue.id);
                            event.stopPropagation();
                        }}>
                        {strings.ChangePermissionsText}
                    </button>
                    <span className={sharingTipStyles.divider}> {' | '} </span>
                </>
            )}
            {canIgnore && (
                <button
                    className={sharingTipStyles.actionButton}
                    onClick={event => {
                        lazyIgnoreSharingTip
                            .import()
                            .then(ignoreSharingTip =>
                                ignoreSharingTip(sharingLinkInfo.linkId, sharingIssue.id)
                            );
                        logIgnoreSharingTipDatapoint(sharingIssue.id);
                        event.stopPropagation();
                    }}>
                    {strings.SuggestActionIgnoreExpiration}
                </button>
            )}
        </div>
    );
}
